import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { LanguageLevel } from 'searchality-data';
import { EMAIL_REGEX } from 'validations';
import DOMPurify from 'dompurify';

function isTouchDevice(): boolean {
  const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');
  const mq = function (query: string) {
    return window.matchMedia(query).matches;
  };

  if ('ontouchstart' in window || 'DocumentTouch' in window) {
    return true;
  }

  const query = ['(', prefixes.join('touch-enabled),('), 'ncoded', ')'].join(
    '',
  );
  return mq(query);
}

export const parseStringValue = (value: any) =>
  /^\d+$/.test(value)
    ? +value
    : /^(true|false)$/.test(value)
    ? JSON.parse(value)
    : value;

export function convertFormDataToJSONObject(formData: FormData) {
  const obj: any = {};
  formData.forEach((val, key) => {
    const isArray = key.includes('[]') || key.includes('files');

    if (isArray) {
      const newKey = key.split('[]')[0];
      if (!obj[newKey]) obj[newKey] = [];
      obj[newKey].push(parseStringValue(val));
    } else {
      obj[key] = parseStringValue(val);
    }
  });
  return obj;
}

export const convertObjToFormData = (
  obj: Record<string, any>,
  formData = new FormData(),
  path = '',
) => {
  if (obj === undefined) return;

  for (const prop in obj) {
    const newPath = path ? `${path}[${prop}]` : prop;
    if (typeof obj[prop] !== 'object') {
      if (obj[prop] instanceof File) {
        const file: File = obj[prop];
        formData.append(newPath, file, file.name);
      } else {
        formData.append(newPath, obj[prop]);
      }
    } else if (obj[prop] === null) {
      formData.append(newPath, obj[prop]);
    } else {
      convertObjToFormData(obj[prop], formData, newPath);
    }
  }

  return formData;
};

export function debounce(
  func: (...args: any[]) => void,
  wait: number,
  isImmediate = false,
) {
  let timeout: NodeJS.Timeout | null;
  return function (...args: any[]) {
    const later = () => {
      timeout = null;
      if (!isImmediate) {
        func(...args);
      }
    };
    const callNow = isImmediate && !timeout;
    if (timeout) clearTimeout(timeout);
    timeout = global.setTimeout(later, wait);
    if (callNow) {
      func(...args);
    }
  };
}
const isAxiosError = (e: AxiosError | unknown): e is AxiosError =>
  'message' in (e as AxiosError) || 'response' in (e as AxiosError);

function toastError(e: AxiosError | unknown) {
  if (isAxiosError(e)) {
    console.error({ text: e.message || (e?.response.data as any)?.message });
    return;
  }

  console.error({ text: 'Error' });
}

const arrayBufferToBlob = (buffer: ArrayBuffer, responseType: string) => {
  return new Blob([new Uint8Array(buffer)], { type: responseType });
};

const getPaginationPages = (currentStep: number, lastPage: number) =>
  lastPage < 6
    ? Array(lastPage)
        .fill(1)
        .map((number, index) => number + index)
    : currentStep < 4
    ? [1, 2, 3, 4, '...', lastPage]
    : currentStep >= lastPage - 2
    ? [1, '...', lastPage - 3, lastPage - 2, lastPage - 1, lastPage]
    : [
        1,
        '...',
        currentStep - 1,
        currentStep,
        currentStep + 1,
        '...',
        lastPage,
      ];

function getStringError(error: any) {
  if (!error?.data) return "Couldn't parse error";

  let {
    data: { message },
  } = error;

  message = message || error.message;

  if (!message) return "Couldn't parse error";

  message =
    typeof message === 'string'
      ? message
      : typeof message?.[0] === 'string'
      ? message[0]
      : 'Invalid data';

  return message;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
function noop(): any {}

export const scrollToRef = (
  ref: React.MutableRefObject<HTMLDivElement>,
  scrollOptions: ScrollIntoViewOptions = { behavior: 'smooth', block: 'start' },
) => {
  ref?.current.scrollIntoView(scrollOptions);
};

export const scrollToFirstValidationError = (
  scrollOptions: ScrollIntoViewOptions = {
    behavior: 'smooth',
    block: 'center',
  },
): void => {
  document.querySelector(`[class*="error"]`)?.scrollIntoView(scrollOptions);
};

function getKeyByValue(object: Record<string, string>, value: string) {
  return Object.keys(object).find((key) => object[key] === value);
}

function lightOrDark(initialColor: string) {
  if (!initialColor) return;

  // Variables for red, green, blue values
  let r, g, b;
  let color;
  // Check the format of the color, HEX or RGB?
  if (initialColor.match(/^rgb/)) {
    // If RGB --> store the red, green, blue values in separate variables
    color = initialColor.match(
      /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/,
    );

    r = parseInt(color[1]);
    g = parseInt(color[2]);
    b = parseInt(color[3]);
  } else {
    // If hex --> Convert it to RGB: http://gist.github.com/983661
    color = +(
      '0x' +
      initialColor.slice(1).replace(initialColor.length < 5 && /./g, '$&$&')
    );

    r = color >> 16;
    g = (color >> 8) & 255;
    b = color & 255;
  }

  // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

  // Using the HSP value, determine whether the color is light or dark
  if (hsp > 127.5) {
    return 'light';
  } else {
    return 'dark';
  }
}

export const getNameInitials = (firstName: string, lastName: string) => {
  if (firstName && lastName) {
    return firstName[0].toUpperCase() + lastName[0].toUpperCase();
  }

  if (firstName) {
    return firstName[0].toUpperCase();
  }

  if (lastName) {
    return lastName[0].toUpperCase();
  }

  return '';
};

export const formatDate = (date: Date | string) => {
  const { day, monthLong, year } = DateTime.fromJSDate(new Date(date));
  return `${day} ${monthLong} ${year}`;
};

export const getEmailsFromString = (email: string) => {
  const newEmailsSet = new Set(
    email
      .split(',')
      .map((el) => el.trim())
      .filter((el) => el.match(EMAIL_REGEX)),
  );

  return Array.from(newEmailsSet);
};

export const numberPriceToCurrencyString = (
  value: number,
  currencySign?: string,
) => {
  const formatedNumber = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0,
  }).format(value);

  if (!currencySign) {
    return formatedNumber;
  }
  return currencySign + formatedNumber.slice(1);
};

const uniqueId = () => {
  const dateString = Date.now().toString(36);
  const randomness = Math.random().toString(36).substr(2);
  return dateString + randomness;
};

const calculateSortValue = (prevValue: string, currentValue: string) => {
  if (prevValue === currentValue) {
    return `-${currentValue.split(' ').join(' -')}`;
  }
  return currentValue;
};

const getPairs = (obj: Record<string, any>, keys: string[]) =>
  Object.entries(obj).reduce((pairs, [key, value]) => {
    if (typeof value === 'object')
      pairs.push(...getPairs(value, [...keys, key]));
    else pairs.push([[...keys, key], value]);
    return pairs;
  }, []);

const trimLongName = (name: string, maxLength: number) =>
  name.length > maxLength - 1 ? name.substring(0, maxLength - 1) + '...' : name;

const capitalizeFirstLetter = (value: string) =>
  !value ? '' : value.charAt(0).toUpperCase() + value.slice(1);

const isLanguageLevelOkay = (
  requiredLevel: LanguageLevel,
  level: LanguageLevel,
) => {
  const tmp = Object.values(LanguageLevel);

  return (
    tmp.findIndex((el) => el === level) <=
    tmp.findIndex((el) => el === requiredLevel)
  );
};

function createMarkup(html: string | Node) {
  return {
    __html: DOMPurify.sanitize(html),
  };
}

function convertToOrdinal(dayNumber: number) {
  if (dayNumber < 1 || dayNumber > 31) {
    return 'Invalid day number';
  }

  if (dayNumber >= 11 && dayNumber <= 13) {
    return dayNumber + 'th';
  }

  switch (dayNumber % 10) {
    case 1:
      return dayNumber + 'st';
    case 2:
      return dayNumber + 'nd';
    case 3:
      return dayNumber + 'rd';
    default:
      return dayNumber + 'th';
  }
}

export default {
  noop,
  toastError,
  isTouchDevice,
  parseStringValue,
  convertObjToFormData,
  convertFormDataToJSONObject,
  debounce,
  getStringError,
  getPaginationPages,
  scrollToRef,
  scrollToFirstValidationError,
  getKeyByValue,
  lightOrDark,
  uniqueId,
  calculateSortValue,
  getPairs,
  capitalizeFirstLetter,
  trimLongName,
  arrayBufferToBlob,
  isLanguageLevelOkay,
  createMarkup,
  convertToOrdinal,
};
